import React from "react";
import { formatDateRange } from "../../../../libs";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { PAYRUN_INTEGRATION_STATUS } from "../../../../constants";
import { useCallback, useEffect, useRef, useState } from "react";
import { getExternalPayrunDetails } from "../../../actions/payrun";
import { PayrunExtended } from "@xemplo/gp-types";

export interface ExternalPayrunDetailsProps {
  payrun: PayrunExtended;
}

interface ExternalPayrunFailedProps {
  message: string;
}

const ExternalPayrunPending = () => {
  return (
    <span>
      Pending{" "}
      <Tooltip
        title="Payrun information will be available once external payrun creation and linking is complete."
        placement={"bottom"}
      >
        <InfoCircleOutlined />
      </Tooltip>
    </span>
  );
};

const ExternalPayrunFailed = (props: ExternalPayrunFailedProps) => {
  return (
    <span className="ant-btn ant-btn-default ant-btn-sm rounded-[20px] bg-danger">
      Failed{" "}
      <Tooltip title={props.message} placement={"bottom"}>
        <InfoCircleOutlined />
      </Tooltip>
    </span>
  );
};

export const ExternalPayrunDetails = (
  props: Readonly<ExternalPayrunDetailsProps>
) => {
  const { payrun } = props;
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [isExternanalPayrunPending, setExternalPayrunPending] =
    useState<boolean>(payrun.isCreateExternalPayrunPending);

  const [isExternalPayrunFailed, setExternalPayrunFailed] = useState<boolean>(
    !payrun.isCreateExternalPayrunPending &&
      payrun.createExternalPayrunDetails?.createPayrunTransactionStatusId ===
        PAYRUN_INTEGRATION_STATUS.FAILED
  );

  const [externalPayrunFailedMessage, setExternalPayrunFailedMessage] =
    useState<string>(
      payrun.createExternalPayrunDetails?.createPayrunTransactionStatus || ""
    );

  const [externalPayrunName, setExternalPayrunName] = useState<string | null>(
    payrun?.externalPayrunName
  );
  const [externalPayrunId, setExternalPayruId] = useState<string | null>(
    payrun?.externalPayrunId
  );

  const handlePayrunStatus = useCallback((statusId: number, result: any) => {
    const isFailed = statusId === PAYRUN_INTEGRATION_STATUS.FAILED;
    const isSuccess = statusId === PAYRUN_INTEGRATION_STATUS.CREATED_AND_LINKED;

    if (isFailed) {
      setExternalPayrunFailed(true);
      setExternalPayrunFailedMessage(result.createPayrunTransactionStatus);
      return true;
    }

    if (isSuccess) {
      setExternalPayrunName(result.externalPayrunName);
      setExternalPayruId(result.externalPayrunId);
      return true;
    }

    setExternalPayrunPending(true);
    return false;
  }, []);

  const getExternalPayrunDetail = useCallback(async () => {
    const resp = await getExternalPayrunDetails({ id: payrun.id });

    const result = (resp as { data: { result?: any } }).data.result ?? {};
    const statusId = result?.createPayrunTransactionStatusId;

    const shouldStopPolling = handlePayrunStatus(statusId, result);

    if (shouldStopPolling) {
      if (!pollingIntervalRef.current) return;

      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    } else {
      if (pollingIntervalRef.current) return;

      pollingIntervalRef.current = setInterval(() => {
        getExternalPayrunDetail();
      }, 10000); // every 10 seconds
    }
  }, [payrun.id, handlePayrunStatus]);

  useEffect(() => {
    if (!payrun.isCreateExternalPayrunPending) return;

    getExternalPayrunDetail();
  }, [payrun.isCreateExternalPayrunPending, getExternalPayrunDetail]);

  const renderPayrunName = () => {
    if (isExternalPayrunFailed) {
      return <ExternalPayrunFailed message={externalPayrunFailedMessage} />;
    }

    if (isExternanalPayrunPending) {
      return <ExternalPayrunPending />;
    }

    if (externalPayrunName) {
      return formatDateRange({ value: externalPayrunName });
    }

    return null;
  };

  return (
    <>
      {(isExternalPayrunFailed ||
        isExternanalPayrunPending ||
        externalPayrunName) && (
        <li>
          <b>Pay Run Name:</b> {renderPayrunName()}
        </li>
      )}
      {externalPayrunId && (
        <li>
          <b>Pay Run ID:</b> {externalPayrunId}
        </li>
      )}
    </>
  );
};
